import React, { Fragment, useMemo } from 'react';

import classNames from 'classnames';
import _ from 'lodash';

import { useDepartmentsBudgetsReportTableContext } from './context/DepartmentsBudgetsReportTableContext';
import DepartmentsBudgetsReportTableRow from './DepartmentsBudgetsReportTableRow';

import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

export const ROW_TYPE_BUDGET_LINE = 'budget_line';
export const ROW_TYPE_TOTAL = 'total';
export const ROW_TYPE_NET = 'net';

const BUDGET_SUB_COLUMN_WIDTH = 168;

const DepartmentsBudgetsReportTable = ({ actions }) => {
    const { report, budgets, loading, isOptionIncluded, expanded, toggleExpansion, toggleAllExpansion, showComments, markupCategories } =
        useDepartmentsBudgetsReportTableContext();

    const budgetColumnCount = useMemo(() => {
        let countPerBudget = 0;
        if (isOptionIncluded('cash')) {
            countPerBudget += 1;
        }
        if (isOptionIncluded('vik')) {
            countPerBudget += 1;
        }

        // Add markup category columns
        if (markupCategories && markupCategories.length > 0) {
            countPerBudget += markupCategories.length;
        }

        countPerBudget += 1; // Total column

        return countPerBudget;
    }, [isOptionIncluded, showComments]);

    const columnCount = useMemo(() => {
        let count = 3; // Common columns

        const includableColumns = {
            gl_codes: 1,
            detailed_description: 1,
            workforce: 1,
        };

        Object.entries(includableColumns).forEach(([key, value]) => {
            if (isOptionIncluded(key)) {
                count += value; // Add column(s) for each included option
            }
        });

        if (budgets && budgets.length > 0) {
            count += budgets.length * budgetColumnCount; // Budgets columns
        }

        if (budgets && budgets.length > 1) {
            count += 1; // Variance column
        }

        if (showComments) {
            count += 1; // Comment column
        }

        return count;
    }, [isOptionIncluded, budgetColumnCount, budgets, showComments]);

    const budgetSubColumnCount = useMemo(() => {
        let count = 0;
        if (isOptionIncluded('cash')) {
            count += 1;
        }
        if (isOptionIncluded('vik')) {
            count += 1;
        }
        count += 1; // Total column

        return count;
    }, [isOptionIncluded]);

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-min-h-0 tw-gap-y-4">
            <div className="tw-flex tw-justify-between">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                    <div className="tw-flex tw-gap-x-1.5">
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion()}>
                            {$t('Expand all')}
                        </button>
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion(false)}>
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                {typeof actions === 'function' ? actions({ data: report }) : actions}
            </div>
            <div className="tw-min-h-0">
                <div className="inbud-table_wrapper tw-h-full tw-overflow-auto">
                    <table className="inbud-table inbud-table_first-sticky">
                        <thead className="tw-sticky tw-top-0 tw-z-10">
                            <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                                {/* Common columns */}
                                <th rowSpan={2} className="tw-z-10 tw-w-1/2 tw-min-w-56">
                                    {$t('Department / Cost Centre')}
                                </th>
                                {isOptionIncluded('detailed_description') && (
                                    <th rowSpan={2} className="tw-w-1/2 tw-min-w-56">
                                        {$t('Detailed Description')}
                                    </th>
                                )}
                                {isOptionIncluded('gl_codes') && (
                                    <th rowSpan={2} className="tw-min-w-40 tw-max-w-40">
                                        {$t('GL Code')}
                                    </th>
                                )}
                                {isOptionIncluded('workforce') && (
                                    <th rowSpan={2} className={classNames('tw-min-w-32 tw-max-w-32', { '!tw-border-r-0': !budgets?.length })}>
                                        {$t('Type (Workforce)')}
                                    </th>
                                )}

                                {/* Budgets columns */}
                                {budgets?.length > 0 &&
                                    budgets.map((budget) => (
                                        <th
                                            key={budget.id}
                                            colSpan={budgetColumnCount}
                                            className={classNames('!tw-pb-1.5 tw-border-b-2', {
                                                '!tw-border-r-0': budgets?.length === 1 && !showComments,
                                            })}
                                            style={{
                                                minWidth: `${budgetSubColumnCount * BUDGET_SUB_COLUMN_WIDTH}px`,
                                                maxWidth: `${budgetSubColumnCount * BUDGET_SUB_COLUMN_WIDTH}px`,
                                            }}
                                        >
                                            {budget.name}
                                        </th>
                                    ))}

                                {/* Variance column */}
                                {budgets?.length > 1 && (
                                    <th rowSpan={2} className={classNames('tw-min-w-32 tw-max-w-32', { '!tw-border-r-0': !showComments })}>
                                        {$t('Variance')}
                                    </th>
                                )}

                                {/* Comment column */}
                                {showComments && (
                                    <th rowSpan={2} className="tw-min-w-128 tw-max-w-128 !tw-border-r-0">
                                        {$t('Comment')}
                                    </th>
                                )}
                            </tr>
                            <tr
                                className={classNames('*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2', {
                                    'last:*:tw-border-r-0': budgets?.length === 1 && !showComments,
                                })}
                            >
                                {/* Budgets columns */}
                                {budgets?.length > 0 &&
                                    budgets.map((budget) => (
                                        <Fragment key={budget.id}>
                                            {isOptionIncluded('cash') && <th className="tw-min-w-28">{$t('Cash')}</th>}
                                            {isOptionIncluded('vik') && <th className="tw-min-w-28">{$t('VIK')}</th>}
                                            {markupCategories &&
                                                markupCategories.length > 0 &&
                                                markupCategories.map((category) => (
                                                    <th key={category.id} className="tw-min-w-28">
                                                        {category.name}
                                                    </th>
                                                ))}
                                            <th className="tw-min-w-28">{$t('Total')}</th>
                                        </Fragment>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                            {!loading && !report && (
                                <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                    <td colSpan={columnCount} className="tw-p-4">
                                        <span>{$t('No data available')}</span>
                                    </td>
                                </tr>
                            )}
                            {!loading && report && (
                                <Fragment>
                                    {/* Revenues */}
                                    <tr className="tw-font-medium !tw-bg-charcoal-75">
                                        <td className="!tw-border-r-0 tw-text-white">{$t('Revenue')}</td>
                                        <td colSpan={columnCount - 1}></td>
                                    </tr>
                                    {report.revenues.groups.map((revenueDepartmentGroup) => (
                                        <Fragment key={revenueDepartmentGroup.id}>
                                            {/* Department Group */}
                                            <DepartmentsBudgetsReportTableRow
                                                lineId={revenueDepartmentGroup.id}
                                                group_name={revenueDepartmentGroup.name}
                                                description={revenueDepartmentGroup.description}
                                                budget={revenueDepartmentGroup.budget}
                                                compareBudget={revenueDepartmentGroup.compareBudget}
                                                toggleHandler={() => toggleExpansion('revenue', revenueDepartmentGroup.id)}
                                                disableExpand={revenueDepartmentGroup.lines.length === 0 && revenueDepartmentGroup.groups.length === 0}
                                                expanded={_.get(expanded, ['revenue', revenueDepartmentGroup.id, 'expanded'], false)}
                                                expandable
                                                show
                                            />

                                            {/* Cost Centers */}
                                            {revenueDepartmentGroup.groups.map((revenueCostCenterGroup) => (
                                                <Fragment key={revenueCostCenterGroup.id}>
                                                    {/* Cost Center Group */}
                                                    <DepartmentsBudgetsReportTableRow
                                                        lineId={revenueCostCenterGroup.id}
                                                        group_name={revenueCostCenterGroup.name}
                                                        description={revenueCostCenterGroup.description}
                                                        budget={revenueCostCenterGroup.budget}
                                                        compareBudget={revenueCostCenterGroup.compareBudget}
                                                        level={1}
                                                        show={_.get(expanded, ['revenue', revenueDepartmentGroup.id, 'expanded'], false)}
                                                        toggleHandler={() => toggleExpansion('revenue', revenueDepartmentGroup.id, revenueCostCenterGroup.id)}
                                                        disableExpand={revenueCostCenterGroup.lines.length === 0}
                                                        expanded={_.get(
                                                            expanded,
                                                            ['revenue', revenueDepartmentGroup.id, 'costcenters', revenueCostCenterGroup.id, 'expanded'],
                                                            false,
                                                        )}
                                                        expandable
                                                    />

                                                    {/* Cost Center Revenues */}
                                                    {revenueCostCenterGroup.lines.map((revenue) => (
                                                        <DepartmentsBudgetsReportTableRow
                                                            key={revenue.id}
                                                            lineId={revenue.id}
                                                            description={revenue.description}
                                                            group_name={revenue.name}
                                                            workforce_type={revenue.workforce_type}
                                                            budget={revenue.budget}
                                                            compareBudget={revenue.compareBudget}
                                                            level={2}
                                                            show={
                                                                _.get(
                                                                    expanded,
                                                                    [
                                                                        'revenue',
                                                                        revenueDepartmentGroup.id,
                                                                        'costcenters',
                                                                        revenueCostCenterGroup.id,
                                                                        'expanded',
                                                                    ],
                                                                    false,
                                                                ) && _.get(expanded, ['revenue', revenueDepartmentGroup.id, 'expanded'], false)
                                                            }
                                                        />
                                                    ))}
                                                </Fragment>
                                            ))}

                                            {/* Department Revenues */}
                                            {revenueDepartmentGroup.lines.map((revenue) => (
                                                <DepartmentsBudgetsReportTableRow
                                                    key={revenue.id}
                                                    lineId={revenue.id}
                                                    description={revenue.description}
                                                    group_name={revenue.name}
                                                    workforce_type={revenue.workforce_type}
                                                    budget={revenue.budget}
                                                    compareBudget={revenue.compareBudget}
                                                    level={1}
                                                    show={_.get(expanded, ['revenue', revenueDepartmentGroup.id, 'expanded'], false)}
                                                />
                                            ))}
                                        </Fragment>
                                    ))}

                                    {/* Total Revenues */}
                                    <DepartmentsBudgetsReportTableRow
                                        lineId={report.revenues.id}
                                        group_name={$t('Total Revenue')}
                                        description={report.revenues.description}
                                        budget={report.revenues.budget}
                                        compareBudget={report.revenues.compareBudget}
                                        type={ROW_TYPE_TOTAL}
                                        show
                                    />

                                    {/* Expenses */}
                                    <tr className="tw-font-medium !tw-bg-charcoal-75">
                                        <td className="!tw-border-r-0 tw-text-white">{$t('Expenditure')}</td>
                                        <td colSpan={columnCount - 1}></td>
                                    </tr>
                                    {report.expenses.groups.map((expenseDepartmentGroup) => (
                                        <Fragment key={expenseDepartmentGroup.id}>
                                            {/* Department Group */}
                                            <DepartmentsBudgetsReportTableRow
                                                lineId={expenseDepartmentGroup.id}
                                                group_name={expenseDepartmentGroup.name}
                                                description={expenseDepartmentGroup.description}
                                                budget={expenseDepartmentGroup.budget}
                                                compareBudget={expenseDepartmentGroup.compareBudget}
                                                toggleHandler={() => toggleExpansion('expense', expenseDepartmentGroup.id)}
                                                disableExpand={
                                                    expenseDepartmentGroup.lines.length === 0 &&
                                                    expenseDepartmentGroup.groups.length === 0 &&
                                                    expenseDepartmentGroup.subgroups?.length === 0
                                                }
                                                expanded={_.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)}
                                                expandable
                                                show
                                            />

                                            {/* Department Expenses */}
                                            {expenseDepartmentGroup.lines.map((expense) => (
                                                <DepartmentsBudgetsReportTableRow
                                                    key={expense.id}
                                                    lineId={expense.id}
                                                    description={expense.description}
                                                    group_name={expense.name}
                                                    workforce_type={expense.workforce_type}
                                                    budget={expense.budget}
                                                    compareBudget={expense.compareBudget}
                                                    level={1}
                                                    show={_.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)}
                                                />
                                            ))}

                                            {/* Expense Projects */}
                                            {expenseDepartmentGroup.subgroups?.map((expenseProject) => (
                                                <Fragment key={expenseProject.id}>
                                                    <DepartmentsBudgetsReportTableRow
                                                        lineId={expenseProject.id}
                                                        description={expenseProject.description}
                                                        group_name={expenseProject.name}
                                                        workforce_type={expenseProject.workforce_type}
                                                        budget={expenseProject.budget}
                                                        compareBudget={expenseProject.compareBudget}
                                                        level={1}
                                                        show={_.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)}
                                                        toggleHandler={() => toggleExpansion('expense', expenseDepartmentGroup.id, null, expenseProject.id)}
                                                        disableExpand={expenseProject.lines.length === 0}
                                                        expanded={_.get(
                                                            expanded,
                                                            ['expense', expenseDepartmentGroup.id, 'projects', expenseProject.id, 'expanded'],
                                                            false,
                                                        )}
                                                        expandable
                                                    />

                                                    {expenseProject.lines.map((expense) => (
                                                        <DepartmentsBudgetsReportTableRow
                                                            key={expense.id}
                                                            lineId={expense.id}
                                                            description={expense.description}
                                                            group_name={expense.name}
                                                            workforce_type={expense.workforce_type}
                                                            budget={expense.budget}
                                                            compareBudget={expense.compareBudget}
                                                            level={2}
                                                            show={
                                                                _.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false) &&
                                                                _.get(
                                                                    expanded,
                                                                    ['expense', expenseDepartmentGroup.id, 'projects', expenseProject.id, 'expanded'],
                                                                    false,
                                                                )
                                                            }
                                                        />
                                                    ))}
                                                </Fragment>
                                            ))}

                                            {/* Cost Centers */}
                                            {expenseDepartmentGroup.groups.map((expenseCostCenterGroup) => (
                                                <Fragment key={expenseCostCenterGroup.id}>
                                                    {/* Cost Center Group */}
                                                    <DepartmentsBudgetsReportTableRow
                                                        lineId={expenseCostCenterGroup.id}
                                                        group_name={expenseCostCenterGroup.name}
                                                        description={expenseCostCenterGroup.description}
                                                        budget={expenseCostCenterGroup.budget}
                                                        compareBudget={expenseCostCenterGroup.compareBudget}
                                                        level={1}
                                                        show={_.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)}
                                                        toggleHandler={() => toggleExpansion('expense', expenseDepartmentGroup.id, expenseCostCenterGroup.id)}
                                                        disableExpand={
                                                            expenseCostCenterGroup.lines.length === 0 && expenseCostCenterGroup.subgroups?.length === 0
                                                        }
                                                        expanded={_.get(
                                                            expanded,
                                                            ['expense', expenseDepartmentGroup.id, 'costcenters', expenseCostCenterGroup.id, 'expanded'],
                                                            false,
                                                        )}
                                                        expandable
                                                    />

                                                    {/* Cost Center - Expenses */}
                                                    {expenseCostCenterGroup.lines.map((expense) => (
                                                        <DepartmentsBudgetsReportTableRow
                                                            key={expense.id}
                                                            lineId={expense.id}
                                                            description={expense.description}
                                                            group_name={expense.name}
                                                            workforce_type={expense.workforce_type}
                                                            budget={expense.budget}
                                                            compareBudget={expense.compareBudget}
                                                            level={2}
                                                            show={
                                                                _.get(
                                                                    expanded,
                                                                    [
                                                                        'expense',
                                                                        expenseDepartmentGroup.id,
                                                                        'costcenters',
                                                                        expenseCostCenterGroup.id,
                                                                        'expanded',
                                                                    ],
                                                                    false,
                                                                ) && _.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)
                                                            }
                                                        />
                                                    ))}

                                                    {/* Cost Center - Expense Projects */}
                                                    {expenseCostCenterGroup.subgroups?.map((expenseProject) => (
                                                        <Fragment key={expenseProject.id}>
                                                            <DepartmentsBudgetsReportTableRow
                                                                lineId={expenseProject.id}
                                                                description={expenseProject.description}
                                                                group_name={expenseProject.name}
                                                                budget={expenseProject.budget}
                                                                compareBudget={expenseProject.compareBudget}
                                                                level={2}
                                                                show={
                                                                    _.get(
                                                                        expanded,
                                                                        [
                                                                            'expense',
                                                                            expenseDepartmentGroup.id,
                                                                            'costcenters',
                                                                            expenseCostCenterGroup.id,
                                                                            'expanded',
                                                                        ],
                                                                        false,
                                                                    ) && _.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)
                                                                }
                                                                toggleHandler={() =>
                                                                    toggleExpansion(
                                                                        'expense',
                                                                        expenseDepartmentGroup.id,
                                                                        expenseCostCenterGroup.id,
                                                                        expenseProject.id,
                                                                    )
                                                                }
                                                                disableExpand={expenseProject.lines.length === 0}
                                                                expanded={_.get(
                                                                    expanded,
                                                                    [
                                                                        'expense',
                                                                        expenseDepartmentGroup.id,
                                                                        'costcenters',
                                                                        expenseCostCenterGroup.id,
                                                                        'projects',
                                                                        expenseProject.id,
                                                                        'expanded',
                                                                    ],
                                                                    false,
                                                                )}
                                                                expandable
                                                            />

                                                            {expenseProject.lines.map((expense) => (
                                                                <DepartmentsBudgetsReportTableRow
                                                                    key={expense.id}
                                                                    lineId={expense.id}
                                                                    description={expense.description}
                                                                    group_name={expense.name}
                                                                    workforce_type={expense.workforce_type}
                                                                    budget={expense.budget}
                                                                    compareBudget={expense.compareBudget}
                                                                    level={3}
                                                                    show={
                                                                        _.get(expanded, [
                                                                            'expense',
                                                                            expenseDepartmentGroup.id,
                                                                            'costcenters',
                                                                            expenseCostCenterGroup.id,
                                                                            'projects',
                                                                            expenseProject.id,
                                                                            'expanded',
                                                                        ]) &&
                                                                        _.get(
                                                                            expanded,
                                                                            [
                                                                                'expense',
                                                                                expenseDepartmentGroup.id,
                                                                                'costcenters',
                                                                                expenseCostCenterGroup.id,
                                                                                'expanded',
                                                                            ],
                                                                            false,
                                                                        ) &&
                                                                        _.get(expanded, ['expense', expenseDepartmentGroup.id, 'expanded'], false)
                                                                    }
                                                                />
                                                            ))}
                                                        </Fragment>
                                                    ))}
                                                </Fragment>
                                            ))}
                                        </Fragment>
                                    ))}

                                    {/* Total Expenditures */}
                                    <DepartmentsBudgetsReportTableRow
                                        lineId={report.expenses.id}
                                        group_name={$t('Total Expenditures')}
                                        description={report.expenses.description}
                                        budget={report.expenses.budget}
                                        compareBudget={report.expenses.compareBudget}
                                        type={ROW_TYPE_TOTAL}
                                        show
                                    />

                                    {/* Net Total */}
                                    <DepartmentsBudgetsReportTableRow
                                        lineId={report.net.id}
                                        group_name={$t('Net')}
                                        budget={report.net.budget}
                                        compareBudget={report.net.compareBudget}
                                        type={ROW_TYPE_NET}
                                        show
                                    />
                                </Fragment>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DepartmentsBudgetsReportTable;
